import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, View, Text, Platform } from "react-native";

import moment from "moment";

import { getTimeFormat, padDigits } from "../../../../helpers/utils";
import Toggle from "../Toggle";
import AppTheme from "../../AppTheme";
import ManaTextInput from "../ManaTextInput";

const DATE_FORMAT = "YYYY-MM-DD HH:mm";
const DEBOUNCE_DELAY = 1000;

export default function ManualTimerWidget(props) {
  const { timeslotDate, compareTime, onTimeChanged = () => {} } = props;

  const [hour, setHour] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [meridian, setMeridian] = useState(null);
  const minutesRef = useRef();

  const timeFormat = getTimeFormat(props.use24hFormat);
  const debounceDelay = props.debounceDelay || DEBOUNCE_DELAY;

  useEffect(() => {
    if (props.time) {
      _parseTime(props.time);
    }
  }, []);

  useEffect(() => {
    if (!hour || !minutes) return;

    if (props.pickDuration) {
      _saveNewTime({ hour, minutes });
    } else {
      _computeMeridian(hour);
    }
  }, [hour, minutes]);

  // ------------------------------------- METHODS -------------------------------------
  const updateHour = (val) => {
    const maxVal = props.pickDuration ? 99999 : props.use24hFormat ? 23 : 12;
    setHour(+val <= maxVal ? val : null);

    // Focus on minutes
    if (val >= 10 && +val <= maxVal) {
      if (minutesRef?.current) {
        minutesRef.current.focus();
        minutesRef.current.select();
      }
    }
  };

  const updateMinutes = (val) => {
    setMinutes(val < 60 ? padDigits(val, 2) : null);
  };

  const toggleMeridian = (val) => {
    setMeridian(val);
    _saveNewTime({ hour, minutes, meridian: val });
  };

  // ------------------------------------- HELPERS -------------------------------------
  const _parseTime = (data) => {
    let time = {};
    const hours = data.split(":");
    setHour(hours[0]);

    if (hours[1]) {
      const minutes_meridian = hours[1].split(" ");
      setMinutes(padDigits(minutes_meridian[0], 2));

      // Set meridian
      if (!props.has24Format && minutes_meridian[1]) {
        setMeridian(minutes_meridian[1]);
      }
    } else {
      setMinutes("00");
    }

    console.log(hours, time)

    return time;
  };

  const _computeMeridian = (h) => {
    if (props.use24hFormat) return;

    const compareTimeM = moment(compareTime, "hh:mma");
    const m = compareTimeM.format("a");
    const newTimeM = moment(`${h}:${minutes} ${m}`, "hh:mma");

    if (!props.isStartTime) {
      if (compareTimeM && newTimeM.diff(compareTimeM, "minutes") <= 0) {
        newTimeM.add(12, "hours");
      }
    } else {
      if (compareTimeM && newTimeM.diff(compareTimeM, "minutes") >= 0) {
        newTimeM.add(-12, "hours");
      }
    }

    const newMeridian = newTimeM.format("a").toUpperCase();
    setMeridian(newMeridian);
    _saveNewTime({ hour, minutes, meridian: newMeridian });
  };

  const _saveNewTime = (time) => {
    if (!time) return;

    const nt =
      `${time.hour}:${time.minutes}` +
      (props.use24hFormat ? "" : time.meridian ? " " + time.meridian : "");

    // For Report page
    if (props.pickDuration) {
      return onTimeChanged(nt, props.isStartTime);
    }

    const ntModel = moment(
      `${timeslotDate} ${nt}`,
      "YYYY-MM-DD " + timeFormat,
    ).format(DATE_FORMAT);

    onTimeChanged(ntModel, props.isStartTime);
  };

  return (
    <View
      style={[
        styles.row,
        props.pickDuration ? { justifyContent: "center" } : {},
      ]}
    >
      {props.label ? (
        <Text style={[styles.label, styles.labelOs]}>{props.label}</Text>
      ) : null}

      <View style={styles.timeContainer}>
        {/* Hours */}
        <ManaTextInput
          value={hour || ""}
          onTextChanged={(t) => updateHour(t)}
          style={[styles.input, styles.inputOs]}
          placeholder={props.use24hFormat ? "hh" : "h"}
          keyboardType="number-pad"
          debounceDelay={debounceDelay}
          maxLength={2}
          autoFocus
          hideOutline
        />

        <Text>:</Text>

        {/* Minutes */}
        <ManaTextInput
          ref={minutesRef}
          value={minutes || ""}
          onTextChanged={(t) => updateMinutes(t)}
          style={[styles.input, styles.inputOs]}
          placeholder="mm"
          keyboardType="number-pad"
          hideOutline
          maxLength={2}
          debounceDelay={debounceDelay}
        />
      </View>

      {!props.use24hFormat ? (
        <Toggle
          style={{ minWidth: 120 }}
          selected={meridian}
          options={["AM", "PM"]}
          selectedColor={AppTheme.toggle.blue}
          onToggle={(value) => toggleMeridian(value)}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    paddingHorizontal: 10,
    marginBottom: 5,
  },
  timeContainer: {
    width: 130,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowColor: "#000",
    shadowRadius: 2,
    borderRadius: 2,
  },
  input: {
    fontWeight: "600",
    fontSize: 20,
    letterSpacing: 1,
    color: "#2D3142",
    paddingVertical: 0,
    paddingLeft: 0,
    paddingRight: 0,
    shadowOpacity: 0,
    textAlign: "center",
    marginBottom: 0,
  },

  inputOs: Platform.select({
    ios: {
      maxWidth: 50,
    },
    android: {
      maxWidth: 50,
    },
    web: {},
  }),
  label: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: "#2D3142",
    marginRight: 10,
    minWidth: 70,
  },
  labelOs: Platform.select({
    ios: {
      minWidth: 50,
    },
    android: {
      minWidth: 50,
    },
    web: {},
  }),
});
